import React, { forwardRef } from 'react'
import { useController, UseControllerProps } from 'react-hook-form'
import {
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormControl,
} from '../../../../../../components/ui/form'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { ChevronDown, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getProducts, ProductsParams } from '@/api/productsAPI'

export interface ISelectFormData {
  id: string | number
  title: string
  subtitle?: string
}

interface Props {
  name: string
  label?: string
  data: ISelectFormData[]
}

const SelectFormProducts = React.forwardRef<HTMLButtonElement, Props & UseControllerProps>((props, ref) => {
  const { control, name, label, data } = props;
  const [products, setProducts] = React.useState(data);
  const { fieldState, field } = useController(props);
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<ISelectFormData | undefined>(
    products.find((itm) => itm.id === field.value)
  );
  const [searchQuery, setSearchQuery] = React.useState('');

  React.useEffect(() => {
    setProducts(data);
  }, [data]);

  React.useEffect(() => {
    setSelected(products.find((itm) => itm.id === (Array.isArray(field.value) ? field.value[1] : field.value)));
  }, [field.value, products]);

  const GetProductsData = async (search: string) => {
    const response = await getProducts({
      page: 1,
      per_page: 10,
      search: search,
      type: 'STOCK,RAW,MANUFACTURED,INPUT,ASSET,VARIATION',
    });
    setProducts(
      response.map(({ id, name, salesCode, description }) => ({
        id: id,
        title: salesCode ? `${name}-${salesCode}` : name,
        subtitle: description,
      }))
    );
  };

  React.useEffect(() => {
    GetProductsData(searchQuery);
  }, [searchQuery]);

  const filteredData = products.filter(
    (item) =>
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.subtitle?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = (selectedItem: ISelectFormData) => {
    setIsPopoverOpen(false);
    setSearchQuery('');
    setSelected(selectedItem);
    field.onChange([selectedItem.title, selectedItem.id]);
  };
  return (
    <div className={'w-full'}>
      <FormField
        control={control}
        name={name}
        render={({ field }) => (
          <FormItem>
            {label && <FormLabel>{label}</FormLabel>}
            <FormControl>
              <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    ref={ref}
                    variant="outline"
                    role="combobox"
                    aria-expanded={isPopoverOpen}
                    className="h-fit w-full justify-between font-normal"
                  >
                    {selected ? (
                      <div className="flex flex-col items-start">
                        <span>{selected.title}</span>
                        {selected.subtitle && (
                          <span className="text-xs text-muted-foreground">
                            {selected.subtitle}
                          </span>
                        )}
                      </div>
                    ) : (
                      'Seleccionar...'
                    )}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width]">
                  <div className="px-3 pt-3">
                    <div className="relative mb-2">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Buscar..."
                        className="pl-9"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto"
                    onWheel={e => {
                      const target = e.currentTarget;
                      target.scrollTop += e.deltaY;
                    }}
                  >
                    {filteredData.length > 0 ? (
                      filteredData.map((item) => (
                        <div
                          key={item.id}
                          className="cursor-pointer p-2 hover:bg-muted"
                          onClick={() => handleSelect(item)}
                        >
                          <p className="font-medium">{item.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {item.subtitle}
                          </p>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        No se encontraron productos.
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </FormControl>
            <FormMessage>{fieldState.error?.message}</FormMessage>
          </FormItem>
        )}
      />
    </div>
  );
});

export { SelectFormProducts };
